import type { HeldAssistance } from 'tio-data/requests/held-funds';

export interface HeldFundsApplication {
  applicationId: string;
  applicationName: string | null;
  programName: string | null;
  applicationState: string;
  rows: HeldAssistance[];
}

/**
 * The calendar day of the limits status `as_of_date`, which the held-funds endpoints need as a
 * strict `YYYY-MM-DD`. The status call passes through whatever the limit date resolved to, which
 * is a timestamp (`2026-09-30 20:00:00`) when a company payment schedule dated it.
 *
 * @param asOfDate - the status payload's `as_of_date`
 * @returns the leading `YYYY-MM-DD`, or null when there is none
 */
export function heldFundsDate(asOfDate: string | null | undefined): string | null {
  return asOfDate?.match(/^\d{4}-\d{2}-\d{2}/)?.[0] ?? null;
}

/**
 * How much lowering a hold to `newAmount` frees for the application being approved.
 *
 * @param row - the hold, with the server's `maxFreeable`
 * @param newAmount - the proposed amount in cents
 * @returns cents; 0 when the proposal is not a reduction or the hold frees nothing
 */
export function freedByReduction(
  row: Pick<HeldAssistance, 'amount' | 'maxFreeable'>,
  newAmount: number
): number {
  const released = row.amount - newAmount;
  if (!Number.isFinite(released) || released <= 0 || !row.maxFreeable) return 0;
  return Math.min(released, row.maxFreeable);
}

/**
 * What stops a proposed reduction from being sent, mirroring the server's own refusals:
 * the amount must be whole cents, not negative, and below the current hold.
 *
 * @param row - the hold being reduced
 * @param newAmount - cents, or NaN when the input does not parse
 * @param reason - free text; blank or whitespace-only is refused
 * @returns null when the change may be sent
 */
export function reductionProblem(
  row: Pick<HeldAssistance, 'amount'>,
  newAmount: number,
  reason: string
): 'amount' | 'reason' | null {
  if (!Number.isInteger(newAmount) || newAmount < 0 || newAmount >= row.amount) return 'amount';
  if (!reason.trim()) return 'reason';
  return null;
}

/**
 * @param rows - holds as the server listed them
 * @returns holds grouped under their application, applications in first-seen order
 */
export function groupByApplication(rows: HeldAssistance[]): HeldFundsApplication[] {
  const groups = new Map<string, HeldFundsApplication>();
  for (const row of rows) {
    const group = groups.get(row.applicationId) ?? {
      applicationId: row.applicationId,
      applicationName: row.applicationName,
      programName: row.programName,
      applicationState: row.applicationState,
      rows: [],
    };
    group.rows.push(row);
    groups.set(row.applicationId, group);
  }
  return [...groups.values()];
}

/**
 * What the other applications hold against this one: holds under a limit it does not share
 * (`NO_SHARED_LIMIT`) and emptied holds are listed but left out of the figures.
 *
 * @param rows - holds as the server listed them
 * @returns cents held, and the number of applications holding them
 */
export function heldFundsTotals(rows: HeldAssistance[]): { total: number; applications: number } {
  const counted = rows.filter((row) => row.amount > 0 && row.reasonCode !== 'NO_SHARED_LIMIT');
  return {
    total: counted.reduce((sum, row) => sum + row.amount, 0),
    applications: new Set(counted.map((row) => row.applicationId)).size,
  };
}

/**
 * The badge intent for a held-funds application's state: approved green, awaiting a decision
 * amber, rejected red, anything else (drafts, attending, closed by the system) neutral.
 *
 * @param state - a `TAS.ApplicationState.*` code
 */
export function applicationStateIntent(
  state: string
): 'success' | 'warning' | 'danger' | 'default' {
  const key = state.split('.').pop() ?? '';
  if (key.endsWith('_REJECTED')) return 'danger';
  if (key.startsWith('PENDING_')) return 'warning';
  if (key.endsWith('_APPROVED') || key === 'FULFILLED') return 'success';
  return 'default';
}
